import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import {
    addErrorMsg,
    addSuccessMsg,
} from "../../framework/redux/slices/appSlice";
import { saveFormAnswer } from "../../services/formAnswerService";
import { IIdTitle } from "../../models/common/idTitle";
import { FormFillView } from "./formFillView";
import { LoadingIndicator } from "../framework/loadingIndicator";
import {
    fetchFormTemplate,
    setCurrentTemplate,
} from "../../framework/redux/slices/formTemplateSlice";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";

export const FormView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { formTemplateId } = useParams();

    useAsyncThunkAction(
        () => fetchFormTemplate(formTemplateId),
        {
            onError: (e) => {
                if (e.name !== "AbortError") {
                    dispatch(
                        addErrorMsg({ message: t("forms.formsLoadError") })
                    );
                }
            },
        },
        [formTemplateId]
    );

    useEffect(() => {
        return () => {
            dispatch(setCurrentTemplate(null));
        };
    }, [dispatch]);

    const template = useAppSelector(
        ({ formTemplate }) => formTemplate.currentTemplate
    );

    const [saving, setSaving] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<IIdTitle | null>(
        null
    );
    const [vehicleError, setVehicleError] = useState(false);

    const vehicles = useAppSelector((state) => state.vehicle.idTitles);
    const needsVehicle = template?.targetObjectType === "Vehicle";
    const initialFormData = useMemo(() => ({}), []);
    const attachmentSessionId = useMemo(() => crypto.randomUUID(), []);

    const handleSave = async (formData: unknown) => {
        if (needsVehicle && !selectedVehicle) {
            setVehicleError(true);
            return;
        }

        setSaving(true);
        try {
            await saveFormAnswer({
                formTemplateId: formTemplateId,
                jsonData: formData,
                jsonSchema: template.jsonSchema,
                relatedObjectType: needsVehicle ? "Vehicle" : undefined,
                relatedObjectId: selectedVehicle?.id,
                attachmentSessionId,
            });
            dispatch(addSuccessMsg(t("forms.saveSuccess")));
            navigate(-1);
        } catch (e) {
            console.error(e);
            dispatch(addErrorMsg({ message: t("forms.saveError") }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <MainLayout hasPx topComponent={<PageTitle header={template?.name} />}>
            {!template ? (
                <LoadingIndicator />
            ) : !template.jsonSchema?.schema ? (
                <Box mx={1} mt={2}>
                    <Alert severity="warning">
                        {t("forms.schemaInvalidOrMissing")}
                    </Alert>
                </Box>
            ) : (
                <>
                    {needsVehicle && (
                        <Box mb={2}>
                            <Autocomplete
                                options={vehicles ?? []}
                                getOptionLabel={(option) => option.title}
                                value={selectedVehicle}
                                onChange={(_, value) => {
                                    setSelectedVehicle(value);
                                    setVehicleError(false);
                                }}
                                data-testid="form-vehicle-select"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("forms.selectVehicle")}
                                        required
                                        error={vehicleError}
                                        helperText={
                                            vehicleError
                                                ? t("forms.vehicleRequired")
                                                : undefined
                                        }
                                    />
                                )}
                            />
                        </Box>
                    )}
                    <FormFillView
                        formData={initialFormData}
                        jsonSchema={template.jsonSchema}
                        saving={saving}
                        handleSave={handleSave}
                        attachmentSessionId={attachmentSessionId}
                    />
                </>
            )}
        </MainLayout>
    );
};
